import { lazy, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom'

import { App, HomePage } from './App'
import { MarketingLayout } from './components/MarketingLayout'
import './styles.css'

// Wave-7 B4 — route-level code-split. Every page component below loads
// lazily on first navigation to its route; the App/Layout shell stays
// eager. The pages are named exports, so each dynamic import maps its
// named member onto `default` for React.lazy. The Suspense fallback
// boundary lives in components/Layout.tsx, wrapping the router Outlet, so
// the header + nav shell stays mounted while a page chunk loads.
const AskPage = lazy(() => import('./pages/AskPage').then((m) => ({ default: m.AskPage })))
const BidsPage = lazy(() => import('./pages/BidsPage').then((m) => ({ default: m.BidsPage })))
const BidPackageLevelingPage = lazy(() =>
  import('./pages/BidPackageLevelingPage').then((m) => ({ default: m.BidPackageLevelingPage })),
)
const DrawSetupPage = lazy(() => import('./pages/DrawSetupPage').then((m) => ({ default: m.DrawSetupPage })))
const DrawQueuePage = lazy(() => import('./pages/DrawQueuePage').then((m) => ({ default: m.DrawQueuePage })))
const BudgetPage = lazy(() => import('./pages/BudgetPage').then((m) => ({ default: m.BudgetPage })))
const ChangeOrdersPage = lazy(() => import('./pages/ChangeOrdersPage').then((m) => ({ default: m.ChangeOrdersPage })))
const CommsPage = lazy(() => import('./pages/CommsPage').then((m) => ({ default: m.CommsPage })))
const CompliancePage = lazy(() => import('./pages/CompliancePage').then((m) => ({ default: m.CompliancePage })))
const DigestsPage = lazy(() => import('./pages/DigestsPage').then((m) => ({ default: m.DigestsPage })))
const CommandBoardPage = lazy(() => import('./pages/CommandBoardPage').then((m) => ({ default: m.CommandBoardPage })))
const DecisionsPage = lazy(() => import('./pages/DecisionsPage').then((m) => ({ default: m.DecisionsPage })))
const DrawsPage = lazy(() => import('./pages/DrawsPage').then((m) => ({ default: m.DrawsPage })))
const FactsPage = lazy(() => import('./pages/FactsPage').then((m) => ({ default: m.FactsPage })))
const FinancialsPage = lazy(() => import('./pages/FinancialsPage').then((m) => ({ default: m.FinancialsPage })))
const InsightsPage = lazy(() => import('./pages/InsightsPage').then((m) => ({ default: m.InsightsPage })))
const InspectionsPage = lazy(() => import('./pages/InspectionsPage').then((m) => ({ default: m.InspectionsPage })))
const MarketingPage = lazy(() => import('./pages/MarketingPage').then((m) => ({ default: m.MarketingPage })))
const MaterialsPage = lazy(() => import('./pages/MaterialsPage').then((m) => ({ default: m.MaterialsPage })))
const NotesPage = lazy(() => import('./pages/NotesPage').then((m) => ({ default: m.NotesPage })))
const CatalogWorkbenchPage = lazy(() => import('./pages/CatalogWorkbenchPage').then((m) => ({ default: m.CatalogWorkbenchPage })))
const TodayPage = lazy(() => import('./pages/TodayPage').then((m) => ({ default: m.TodayPage })))
const PlanSetDetailPage = lazy(() => import('./pages/PlanSetDetailPage').then((m) => ({ default: m.PlanSetDetailPage })))
const PlansPage = lazy(() => import('./pages/PlansPage').then((m) => ({ default: m.PlansPage })))
const PlanReviewFindingsPage = lazy(() => import('./pages/PlanReviewFindingsPage').then((m) => ({ default: m.PlanReviewFindingsPage })))
const DrawWorkspacePage = lazy(() => import('./pages/DrawWorkspacePage').then((m) => ({ default: m.DrawWorkspacePage })))
const DailyLogsPage = lazy(() => import('./pages/DailyLogsPage').then((m) => ({ default: m.DailyLogsPage })))
const ProfileDetailPage = lazy(() => import('./pages/ProfileDetailPage').then((m) => ({ default: m.ProfileDetailPage })))
const PunchListPage = lazy(() => import('./pages/PunchListPage').then((m) => ({ default: m.PunchListPage })))
const ProfilesPage = lazy(() => import('./pages/ProfilesPage').then((m) => ({ default: m.ProfilesPage })))
const SchedulePage = lazy(() => import('./pages/SchedulePage').then((m) => ({ default: m.SchedulePage })))
const MasterSchedulePage = lazy(() => import('./pages/MasterSchedulePage').then((m) => ({ default: m.MasterSchedulePage })))
const ScopeCascadePage = lazy(() => import('./pages/ScopeCascadePage').then((m) => ({ default: m.ScopeCascadePage })))
const SelectionsPage = lazy(() => import('./pages/SelectionsPage').then((m) => ({ default: m.SelectionsPage })))
const SiteWalksPage = lazy(() => import('./pages/SiteWalks').then((m) => ({ default: m.SiteWalksPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })))
const SpecBookPage = lazy(() => import('./pages/SpecBookPage').then((m) => ({ default: m.SpecBookPage })))
const SubsRosterPage = lazy(() => import('./pages/SubsRosterPage').then((m) => ({ default: m.SubsRosterPage })))
const ProjectRosterPage = lazy(() =>
  import('./pages/ProjectRosterPage').then((m) => ({ default: m.ProjectRosterPage })),
)
const ContractorsPage = lazy(() => import('./pages/ContractorsPage').then((m) => ({ default: m.ContractorsPage })))
const ContractorDetailPage = lazy(() => import('./pages/ContractorDetailPage').then((m) => ({ default: m.ContractorDetailPage })))
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage').then((m) => ({ default: m.TestimonialsPage })))
const W9Page = lazy(() => import('./pages/W9Page').then((m) => ({ default: m.W9Page })))
const WebsiteLeadsPage = lazy(() => import('./pages/WebsiteLeadsPage').then((m) => ({ default: m.WebsiteLeadsPage })))
const MarketingImportPage = lazy(() => import('./pages/MarketingImportPage').then((m) => ({ default: m.MarketingImportPage })))
const MarketingSweepPage = lazy(() => import('./pages/MarketingSweepPage').then((m) => ({ default: m.MarketingSweepPage })))
const MarketingContactsPage = lazy(() => import('./pages/MarketingContactsPage').then((m) => ({ default: m.MarketingContactsPage })))
const EmailHealthPage = lazy(() => import('./pages/EmailHealthPage').then((m) => ({ default: m.EmailHealthPage })))
const MarketingSmartListsPage = lazy(() => import('./pages/MarketingSmartListsPage').then((m) => ({ default: m.MarketingSmartListsPage })))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage').then((m) => ({ default: m.CampaignsPage })))
const LotProspectsPage = lazy(() => import('./pages/LotProspectsPage').then((m) => ({ default: m.LotProspectsPage })))
const LeadScoresPage = lazy(() => import('./pages/LeadScoresPage').then((m) => ({ default: m.LeadScoresPage })))
const ReactivationPage = lazy(() => import('./pages/ReactivationPage').then((m) => ({ default: m.ReactivationPage })))
const MarketingOverviewPage = lazy(() => import('./pages/MarketingOverviewPage').then((m) => ({ default: m.MarketingOverviewPage })))
const GbpPage = lazy(() => import('./pages/GbpPage').then((m) => ({ default: m.GbpPage })))
const PartnerNetworkPage = lazy(() => import('./pages/PartnerNetworkPage').then((m) => ({ default: m.PartnerNetworkPage })))
const WelcomeHomePage = lazy(() => import('./pages/WelcomeHomePage').then((m) => ({ default: m.WelcomeHomePage })))
const WizardPage = lazy(() => import('./pages/Wizard').then((m) => ({ default: m.WizardPage })))

// Patch 015.3 — Command Board demoted from primary user surface to
// admin-internal. The old /projects/:id/plan/command path redirects to
// /admin/command-board/:id so existing links + bookmarks keep resolving
// ("kept addressable for diagnostics" per the 015.3 directive).
function CommandBoardRedirect(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>()
  return (
    <Navigate to={`/admin/command-board/${projectId ?? ''}`} replace />
  )
}

// Step 1c — Decisions moved from Build to Plan (BUILD_DIRECTION §7). The
// old /projects/:id/build/decisions path redirects to /plan/decisions so
// existing links + bookmarks keep resolving.
function DecisionsRedirect(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>()
  return (
    <Navigate to={`/projects/${projectId ?? ''}/plan/decisions`} replace />
  )
}

const root = document.getElementById('root')
if (!root) throw new Error('root element missing')

createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<App />}>
          <Route index element={<HomePage />} />
          <Route path="profiles" element={<ProfilesPage />} />
          <Route path="profiles/:profileId" element={<ProfileDetailPage />} />
          <Route
            path="projects/:projectId/setup"
            element={<WizardPage />}
          />
          <Route
            path="projects/:projectId/today"
            element={<TodayPage />}
          />
          <Route
            path="projects/:projectId/ask"
            element={<AskPage />}
          />
          <Route
            path="projects/:projectId/plan"
            element={<Navigate to="scope" replace />}
          />
          <Route
            path="projects/:projectId/plan/command"
            element={<CommandBoardRedirect />}
          />
          <Route
            path="admin/command-board/:projectId"
            element={<CommandBoardPage />}
          />
          <Route
            path="admin/catalog"
            element={<CatalogWorkbenchPage />}
          />
          <Route path="projects/:projectId/plan/facts" element={<FactsPage />} />
          <Route
            path="projects/:projectId/plan/scope"
            element={<ScopeCascadePage />}
          />
          <Route path="projects/:projectId/plan/plans" element={<PlansPage />} />
          <Route
            path="projects/:projectId/plan/plans/:planSetId"
            element={<PlanSetDetailPage />}
          />
          <Route
            path="projects/:projectId/plan/review-findings"
            element={<PlanReviewFindingsPage />}
          />
          <Route
            path="projects/:projectId/plan/spec-book"
            element={<SpecBookPage />}
          />
          <Route
            path="projects/:projectId/plan/decisions"
            element={<DecisionsPage />}
          />
          <Route
            path="projects/:projectId/plan/selections"
            element={<SelectionsPage />}
          />
          <Route
            path="projects/:projectId/build"
            element={<Navigate to="schedule" replace />}
          />
          <Route
            path="projects/:projectId/build/schedule"
            element={<SchedulePage />}
          />
          <Route
            path="projects/:projectId/build/timeline"
            element={<MasterSchedulePage />}
          />
          <Route
            path="projects/:projectId/build/inspections"
            element={<InspectionsPage />}
          />
          <Route
            path="projects/:projectId/build/walks"
            element={<SiteWalksPage />}
          />
          <Route
            path="projects/:projectId/build/daily-logs"
            element={<DailyLogsPage />}
          />
          <Route
            path="projects/:projectId/build/punch"
            element={<PunchListPage />}
          />
          <Route
            path="projects/:projectId/build/materials"
            element={<MaterialsPage />}
          />
          <Route
            path="projects/:projectId/build/notes"
            element={<NotesPage />}
          />
          <Route
            path="projects/:projectId/build/decisions"
            element={<DecisionsRedirect />}
          />
          <Route
            path="projects/:projectId/money"
            element={<Navigate to="budget" replace />}
          />
          <Route
            path="projects/:projectId/money/budget"
            element={<BudgetPage />}
          />
          <Route
            path="projects/:projectId/money/bids"
            element={<BidsPage />}
          />
          <Route
            path="projects/:projectId/money/bids/:bidPackageId"
            element={<BidPackageLevelingPage />}
          />
          <Route
            path="projects/:projectId/money/changes"
            element={<ChangeOrdersPage />}
          />
          <Route
            path="projects/:projectId/money/draw-workspace"
            element={<DrawWorkspacePage />}
          />
          <Route
            path="projects/:projectId/money/draws"
            element={<DrawsPage />}
          />
          <Route
            path="projects/:projectId/money/draw-setup"
            element={<DrawSetupPage />}
          />
          <Route
            path="projects/:projectId/money/draw-queue"
            element={<DrawQueuePage />}
          />
          <Route
            path="projects/:projectId/money/financials"
            element={<FinancialsPage />}
          />
          <Route
            path="projects/:projectId/people"
            element={<CommsPage />}
          />
          <Route
            path="projects/:projectId/people/comms"
            element={<CommsPage />}
          />
          <Route
            path="projects/:projectId/people/digests"
            element={<DigestsPage />}
          />
          <Route
            path="projects/:projectId/people/subs"
            element={<SubsRosterPage />}
          />
          <Route
            path="projects/:projectId/people/roster"
            element={<ProjectRosterPage />}
          />
          <Route
            path="projects/:projectId/people/contractors"
            element={<ContractorsPage />}
          />
          <Route
            path="projects/:projectId/people/contractors/:contractorId"
            element={<ContractorDetailPage />}
          />
          <Route
            path="projects/:projectId/people/welcome-home"
            element={<WelcomeHomePage />}
          />
          <Route path="projects/:projectId/marketing" element={<MarketingLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<MarketingOverviewPage />} />
            <Route path="feed" element={<MarketingPage />} />
            <Route path="gbp" element={<GbpPage />} />
            <Route path="leads" element={<WebsiteLeadsPage />} />
            <Route path="testimonials" element={<TestimonialsPage />} />
            <Route path="import" element={<MarketingImportPage />} />
            <Route path="sweep" element={<MarketingSweepPage />} />
            <Route path="contacts" element={<MarketingContactsPage />} />
            <Route path="email" element={<EmailHealthPage />} />
            <Route path="lists" element={<MarketingSmartListsPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="lots" element={<LotProspectsPage />} />
            <Route path="lead-scores" element={<LeadScoresPage />} />
            <Route path="reactivation" element={<ReactivationPage />} />
            <Route path="partners" element={<PartnerNetworkPage />} />
          </Route>
          <Route
            path="projects/:projectId/ops"
            element={<W9Page />}
          />
          <Route
            path="projects/:projectId/ops/settings"
            element={<SettingsPage />}
          />
          <Route
            path="projects/:projectId/ops/compliance"
            element={<CompliancePage />}
          />
          <Route
            path="projects/:projectId/ops/insights"
            element={<InsightsPage />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  </StrictMode>,
)
